import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  IAddAiPromptTemplateRequest,
  IUpdateAiPromptTemplateRequest,
} from "../../../@types/admin/AdminAiPromptTemplate";
import { aiPromptTemplatesService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { AI_PROMPT_TEMPLATES_QUERY_KEY } from "../../queries/admin/aiPromptTemplates";

function useInvalidateAiPromptTemplates() {
  const queryClient = useQueryClient();

  return (id?: string) => {
    queryClient.invalidateQueries({
      queryKey: [AI_PROMPT_TEMPLATES_QUERY_KEY],
    });
    if (id) {
      queryClient.invalidateQueries({
        queryKey: ["admin-ai-prompt-template", id],
      });
    }
  };
}

export function useAddAiPromptTemplate() {
  const invalidate = useInvalidateAiPromptTemplates();

  const { mutateAsync, isPending } = useMutation<
    void,
    Error,
    IAddAiPromptTemplateRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await aiPromptTemplatesService.addAiPromptTemplate(request)),
    onSuccess: () => invalidate(),
  });

  return { addAiPromptTemplate: mutateAsync, isAddingTemplate: isPending };
}

export function useUpdateAiPromptTemplate() {
  const invalidate = useInvalidateAiPromptTemplates();

  const { mutateAsync, isPending } = useMutation<
    void,
    Error,
    IUpdateAiPromptTemplateRequest
  >({
    mutationFn: async (request) =>
      ensureOk(await aiPromptTemplatesService.updateAiPromptTemplate(request)),
    onSuccess: (_data, request) => invalidate(request.id),
  });

  return { updateAiPromptTemplate: mutateAsync, isUpdatingTemplate: isPending };
}
